/**
 * Study-sheet copy for each navagraha (Jyotish → Grahas).
 * Classical details follow common Paraashara-style summaries; nodes vary by school.
 */

export const GRAHA_GUIDE_ORDER = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];

export const GRAHA_GUIDE_HUB = {
  Sun: { emoji: '☀️', blurb: 'Soul, father, vitality, clear authority' },
  Moon: { emoji: '🌙', blurb: 'Mind, mother, care, emotional tides' },
  Mars: { emoji: '🔥', blurb: 'Courage, effort, heat, straight action' },
  Mercury: { emoji: '💬', blurb: 'Speech, trade, skill, clever movement' },
  Jupiter: { emoji: '🙏', blurb: 'Wisdom, dharma, teachers, expansion' },
  Venus: { emoji: '✨', blurb: 'Harmony, arts, partnership, comfort' },
  Saturn: { emoji: '🪐', blurb: 'Time, duty, karma, slow mastery' },
  Rahu: { emoji: '◐', blurb: 'Craving, novelty, unconventional paths' },
  Ketu: { emoji: '◑', blurb: 'Release, insight, past-life thread' },
};

/** @typedef {'amber'|'slate'|'rose'|'cyan'|'violet'|'emerald'|'indigo'|'purple'|'teal'} GrahaTheme */

/** @type {Record<string, GrahaTheme>} */
export const GRAHA_GUIDE_THEME = {
  Sun: 'amber',
  Moon: 'slate',
  Mars: 'rose',
  Mercury: 'cyan',
  Jupiter: 'violet',
  Venus: 'emerald',
  Saturn: 'indigo',
  Rahu: 'purple',
  Ketu: 'teal',
};

export const GRAHA_GUIDES = {
  Sun: {
    titleEn: 'Surya (Sun), must-know points',
    taglineHi: 'सूर्य: आत्मा, प्राण, गरिमा',
    heroEmoji: '☀️',
    intro:
      'A study sheet, not a verdict. The Sun is visibility in Jyotish - clarity, dignity, and the chart’s sense of centre. Harsh when afflicted; life-giving when supported.',
    goldenRule: 'The Sun shows where life asks you to stand upright - not to dominate, but to be authentic.',
    goldenRuleNote: 'Strength shows as steady confidence; trouble shows as ego battles or burnout. Always read sign, house, and avastha together.',
    reassuring: [
      {
        title: 'Light, not vanity',
        titleHi: 'प्रकाश, अहंकार नहीं',
        body: 'Classical texts praise a strong Sun for courage and nobility. The lesson is proportion - shine without scorching yourself or others.',
      },
      {
        title: 'Father / authority map',
        titleHi: 'पिता / अधिकार',
        body: 'As a natural significator of father and rulers, the Sun colours how we meet authority, inner and outer, not a fixed fate label.',
      },
      {
        title: 'Context wins',
        titleHi: 'संदर्भ ज़रूरी',
        body: 'Exaltation, debilitation, combustion of other grahas, and aspects change the story. Avoid one-word doom or hype.',
      },
    ],
    coreFacts: [
      { label: 'Karaka (significator)', hi: 'कारक', value: 'Soul (aatman), father, kingly duty, vitality, eyes, honour' },
      { label: 'Nature', hi: 'स्वभाव', value: 'Natural malefic by effect (heat, intensity) yet saattvic source, clarity over cruelty when well placed' },
      { label: 'Speed', hi: 'गति', value: 'About one month per sign (rough average)' },
      { label: 'Rashi ownership', hi: 'राशि स्वामी', value: 'सिंह, स्वामित्व राशि (rashi lordship)' },
      { label: 'Exaltation', hi: 'उच्च', value: 'मेष में उच्च; शास्त्रीय रूप से लगभग १०°' },
      { label: 'Debilitation', hi: 'नीच', value: 'तुला में नीच' },
      { label: 'Mooltrikona', hi: 'मूलत्रिकोण', value: 'सिंह, प्रायः ०° – २०° का बैंड (पाठशालाएँ थोड़ा भिन्न)' },
      { label: 'Drishti (aspects)', hi: 'दृष्टि', value: '7th from its sign only (full aspect)' },
      { label: 'Vakri (retrograde)', hi: 'वक्र', value: 'The Sun does not go retrograde in standard astronomy used in charts' },
      { label: 'Digbala', hi: 'दिग्बल', value: 'Strongest in the 10th house in standard digbala lists' },
    ],
    friendsEnemies: [
      { label: 'Friends', hi: 'मित्र', grahas: 'चंद्र, मंगल, गुरु' },
      { label: 'Enemy', hi: 'शत्रु', grahas: 'शुक्र, शनि, राहु, केतु' },
      { label: 'Neutral', hi: 'सम', grahas: 'बुध' },
    ],
    housesNote: [
      {
        label: 'Kendra / trikona affinity',
        hi: 'केन्द्र / त्रिकोण',
        value: 'Strong angular (1,4,7,10) or trine (1,5,9) emphasis often supports leadership themes, still modulated by dignity.',
      },
      {
        label: '6 / 8 / 12 colour',
        hi: 'दुःस्थान रंग',
        value: 'Dusthana placement can stress vitality or visibility; remedies and culture vary, chart synthesis matters.',
      },
    ],
    lifeLayer: [
      { label: 'Represents', hi: 'कारकत्व', value: 'Government, medicine (light), temples, gold, wheat (traditional lists)' },
      { label: 'Body parts (study map)', hi: 'अंग', value: 'Heart, eyes, bones, correspondences for study, not diagnosis' },
      { label: 'Time & results', hi: 'काल / फल', value: 'Sunday; solar years and authority cycles in dasha layers' },
      { label: 'Transit note', hi: 'गोचर', value: 'Monthly sign changes mark mood of “visibility” in mundane timing chats' },
    ],
    craftSymbols: [
      { label: 'Profession flavour', hi: 'व्यवसाय', value: 'Administration, medicine, teaching authority, leadership roles' },
      { label: 'Colour', hi: 'रंग', value: 'Copper, red, saffron tones' },
      { label: 'Metal', hi: 'धातु', value: 'Gold (traditional)' },
      { label: 'Direction', hi: 'दिशा', value: 'East' },
      { label: 'Day', hi: 'वार', value: 'Sunday (Ravivaara)' },
    ],
    behaviorBullets: [
      'Seeks respect, truth, and clear boundaries, can feel “hot” when challenged.',
      'Best quality: integrity, courage, generous leadership.',
      'Rough edge: pride, rigidity, or exhaustion when overextended.',
    ],
    oneLiner: ' “Surya asks: are you aligned with your own light?”',
    oneLinerNote: 'Not astrology advice, just a memory hook for study.',
  },

  Moon: {
    titleEn: 'Chandra (Moon), must-know points',
    taglineHi: 'चंद्र: मन, मातृ, स्नेह',
    heroEmoji: '🌙',
    intro:
      'The fastest graha - mind, memory, and the tide of comfort. Waxing vs waning, paksha, and the Sun reshape everything. Gentle when supported; restless when frayed.',
    goldenRule: 'The Moon shows how the mind receives life - nourish the chart’s receiver before judging outcomes.',
    goldenRuleNote: 'A strong Moon supports stability; affliction can mean mood volatility, not a moral label.',
    reassuring: [
      {
        title: 'Sensitivity is data',
        titleHi: 'संवेदनशीलता',
        body: 'A difficult Moon often maps coping load, not weakness. Classical authors stress protection and routine (sattva, sleep, care).',
      },
      {
        title: 'Mother / nurture',
        titleHi: 'माता / पोषण',
        body: 'Significator of mother and fluids, helpful for seeing emotional needs and attachment patterns in context.',
      },
      {
        title: 'Tithi & phase',
        titleHi: 'पक्ष',
        body: 'Bright half vs dark half changes tone in many techniques; combine with sign and house, not isolation.',
      },
    ],
    coreFacts: [
      { label: 'Karaka (significator)', hi: 'कारक', value: 'Mind, mother, emotions, public, fluids, travel' },
      { label: 'Nature', hi: 'स्वभाव', value: 'Natural benefic, stronger when waxing and well aspected' },
      { label: 'Speed', hi: 'गति', value: 'About 2.25 days per sign (very fast)' },
      { label: 'Rashi ownership', hi: 'राशि स्वामी', value: 'कर्क, स्वामित्व राशि (rashi lordship)' },
      { label: 'Exaltation', hi: 'उच्च', value: 'वृषभ में उच्च; शास्त्रीय रूप से लगभग ३°' },
      { label: 'Debilitation', hi: 'नीच', value: 'वृश्चिक में नीच' },
      { label: 'Mooltrikona', hi: 'मूलत्रिकोण', value: 'कर्क, प्रायः ४° – ३०° का बैंड (पाठशालाएँ भिन्न)' },
      { label: 'Drishti (aspects)', hi: 'दृष्टि', value: '7th from its sign only (full aspect)' },
      { label: 'Vakri (retrograde)', hi: 'वक्र', value: 'Moon is not retrograde in standard ephemeris treatment' },
      { label: 'Digbala', hi: 'दिग्बल', value: 'Strongest in the 4th house in standard digbala lists' },
    ],
    friendsEnemies: [
      { label: 'Friends', hi: 'मित्र', grahas: 'सूर्य, बुध' },
      { label: 'Enemy', hi: 'शत्रु', grahas: 'राहु, केतु' },
      { label: 'Neutral', hi: 'सम', grahas: 'मंगल, गुरु, शुक्र, शनि' },
    ],
    housesNote: [
      {
        label: 'Water & home',
        hi: 'जल / गृह',
        value: '4th-house themes echo Moon’s digbala, inner safety, mother, dwelling.',
      },
      {
        label: 'Sade Sati anchor',
        hi: 'साढ़े साती',
        value: 'Transits of Saturn relative to natal Moon define the famous 7.5-year period, read with full Gochar context.',
      },
    ],
    lifeLayer: [
      { label: 'Represents', hi: 'कारकत्व', value: 'Milk, rice, silver, crowds, changeability' },
      { label: 'Body parts (study map)', hi: 'अंग', value: 'Blood, breasts, lungs, study correspondences only' },
      { label: 'Time & results', hi: 'काल / फल', value: 'Monday; hourly horaa cycles; fast-changing dasha feel' },
      { label: 'Transit note', hi: 'गोचर', value: 'Moon’s sign changes colour daily emotional “weather”' },
    ],
    craftSymbols: [
      { label: 'Profession flavour', hi: 'व्यवसाय', value: 'Hospitality, nursing, liquids, shipping, public-facing care' },
      { label: 'Colour', hi: 'रंग', value: 'White, pale silver, milky tones' },
      { label: 'Metal', hi: 'धातु', value: 'Silver (traditional)' },
      { label: 'Direction', hi: 'दिशा', value: 'North-west (common note); also linked with watery directions in texts' },
      { label: 'Day', hi: 'वार', value: 'Monday (Somavaara)' },
    ],
    behaviorBullets: [
      'Adapts quickly; needs rhythm, sleep, and emotional honesty.',
      'Best quality: empathy, memory, intuitive glue in relationships.',
      'Rough edge: worry, inconsistency, or overwhelm when afflicted.',
    ],
    oneLiner: ' “Chandra is the mind’s weather report, learn to read the sky.”',
    oneLinerNote: 'Combine paksha, sign, and aspects before summarising a person.',
  },

  Mars: {
    titleEn: 'Mangal (Mars), must-know points',
    taglineHi: 'मंगल: साहस, ऊर्जा, रक्षा',
    heroEmoji: '🔥',
    intro:
      'Heat, courage, and the will to protect. Mars is blunt and fast, excellent for builders and defenders, rough when pride hijacks purpose.',
    goldenRule: 'Mars gives the energy to act; the chart shows whether action serves dharma or burns the house down.',
    goldenRuleNote: 'Channelled Mars is stamina; scattered Mars is friction, yoga and house tell which.',
    reassuring: [
      {
        title: 'Anger as signal',
        titleHi: 'क्रोध संकेत',
        body: 'Affliction can show boundary issues or fatigue, not “bad character.” Many strong Mars people are fiercely loyal.',
      },
      {
        title: 'Siblings & land',
        titleHi: 'भाई / भूमि',
        body: 'Karaka for younger siblings and property disputes in classical glossaries, contextual, not deterministic.',
      },
      {
        title: 'Exercise & craft',
        titleHi: 'श्रम',
        body: 'Physical outlet and honest work often harmonise Mars in real life; the chart mirrors that need.',
      },
    ],
    coreFacts: [
      { label: 'Karaka (significator)', hi: 'कारक', value: 'Courage, siblings (younger), land, surgery, fire, police, competition' },
      { label: 'Nature', hi: 'स्वभाव', value: 'Natural malefic, brave, sharp, pitta-dominant imagery in classics' },
      { label: 'Speed', hi: 'गति', value: 'About 45 days per sign (variable)' },
      { label: 'Rashi ownership', hi: 'राशि स्वामी', value: 'मेष व वृश्चिक, दो स्वामित्व राशियाँ' },
      { label: 'Exaltation', hi: 'उच्च', value: 'मकर में उच्च; शास्त्रीय रूप से लगभग २८°' },
      { label: 'Debilitation', hi: 'नीच', value: 'कर्क में नीच' },
      { label: 'Mooltrikona', hi: 'मूलत्रिकोण', value: 'मेष, प्रायः ०° – १२° का बैंड' },
      { label: 'Drishti (aspects)', hi: 'दृष्टि', value: '4th, 7th, and 10th from its sign' },
      { label: 'Vakri (retrograde)', hi: 'वक्र', value: 'Retro periods intensify inward review of desire, anger, and strategy' },
      { label: 'Digbala', hi: 'दिग्बल', value: 'Strongest in the 10th house (south) in standard digbala lists' },
    ],
    friendsEnemies: [
      { label: 'Friends', hi: 'मित्र', grahas: 'सूर्य, चंद्र, गुरु' },
      { label: 'Enemy', hi: 'शत्रु', grahas: 'बुध' },
      { label: 'Neutral', hi: 'सम', grahas: 'शुक्र, शनि' },
    ],
    housesNote: [
      {
        label: 'Upachaya',
        hi: 'उपचय',
        value: 'Houses 3, 6, 10, 11 can reward sustained effort, Mars likes problems to solve when dignity helps.',
      },
      {
        label: '8th / surgery imagery',
        hi: 'अष्टम',
        value: 'Links to cuts, healing crises, and transformation in traditional karakatva, never a medical verdict.',
      },
    ],
    lifeLayer: [
      { label: 'Represents', hi: 'कारकत्व', value: 'Copper, red lentils, weapons, engines, athletes' },
      { label: 'Body parts (study map)', hi: 'अंग', value: 'Marrow, blood, muscles, study maps only' },
      { label: 'Time & results', hi: 'काल / फल', value: 'Tuesday; quick sparks vs long Saturn delays in mixed dashas' },
      { label: 'Transit note', hi: 'गोचर', value: 'Mars transits stir argument, hustle, or repair projects, tone from sign/house' },
    ],
    craftSymbols: [
      { label: 'Profession flavour', hi: 'व्यवसाय', value: 'Military, surgery, engineering, sports, fire services' },
      { label: 'Colour', hi: 'रंग', value: 'Red, blood red' },
      { label: 'Metal', hi: 'धातु', value: 'Copper (traditional)' },
      { label: 'Direction', hi: 'दिशा', value: 'South' },
      { label: 'Day', hi: 'वार', value: 'Tuesday (Manggalavaara)' },
    ],
    behaviorBullets: [
      'Direct, protective, impatient with vague promises.',
      'Best quality: bravery, loyalty, gets things moving.',
      'Rough edge: clashes, burns out if rest is ignored.',
    ],
    oneLiner: ' “Mangal protects what you love, aim the spear carefully.”',
    oneLinerNote: 'Aspects to Moon and Venus often soften or complicate the heat.',
  },

  Mercury: {
    titleEn: 'Budha (Mercury), must-know points',
    taglineHi: 'बुध: बुद्धि, वाणी, व्यापार',
    heroEmoji: '💬',
    intro:
      'The negotiator of the sky, speech, numbers, humour, and trade. Mercury is mixed benefic/malefic by company: clever with good allies, anxious when scattered.',
    goldenRule: 'Mercury rewards clear thinking and honest words; the chart shows the topics those words will chase.',
    goldenRuleNote: 'Combust Mercury, retrograde Mercury, and sign dignity change wit into worry or vice versa.',
    reassuring: [
      {
        title: 'Restless mind ≠ failure',
        titleHi: 'चंचल मन',
        body: 'Mercury-heavy charts often need variety and skill-building, not a verdict on depth.',
      },
      {
        title: 'Youth & students',
        titleHi: 'छात्र',
        body: 'Karaka for students and playful curiosity; supports teaching and translation metaphors.',
      },
      {
        title: 'Company matters',
        titleHi: 'संगति',
        body: 'Mercury behaves like planets it sits with, classical dictum “yuti modifies tone.”',
      },
    ],
    coreFacts: [
      { label: 'Karaka (significator)', hi: 'कारक', value: 'Speech, intellect, trade, writing, humour, skin, nieces/nephews' },
      { label: 'Nature', hi: 'स्वभाव', value: 'Benefic/malefic by association, dual, adaptable' },
      { label: 'Speed', hi: 'गति', value: 'About a month per sign (never far from Sun)' },
      { label: 'Rashi ownership', hi: 'राशि स्वामी', value: 'मिथुन व कन्या, दो स्वामित्व राशियाँ' },
      { label: 'Exaltation', hi: 'उच्च', value: 'कन्या में उच्च; शास्त्रीय रूप से लगभग १५°' },
      { label: 'Debilitation', hi: 'नीच', value: 'मीन में नीच' },
      { label: 'Mooltrikona', hi: 'मूलत्रिकोण', value: 'कन्या, प्रायः १६° – २०° का बैंड (पाठशालाएँ भिन्न)' },
      { label: 'Drishti (aspects)', hi: 'दृष्टि', value: '7th from its sign only (full aspect)' },
      { label: 'Vakri (retrograde)', hi: 'वक्र', value: 'Retrograde Mercury: rethink, revise, revisit contracts and data' },
      { label: 'Digbala', hi: 'दिग्बल', value: 'Strongest in the 1st house in standard digbala lists' },
    ],
    friendsEnemies: [
      { label: 'Friends', hi: 'मित्र', grahas: 'सूर्य, शुक्र' },
      { label: 'Enemy', hi: 'शत्रु', grahas: 'चंद्र' },
      { label: 'Neutral', hi: 'सम', grahas: 'मंगल, गुरु, शनि' },
    ],
    housesNote: [
      {
        label: 'Angular wit',
        hi: 'केन्द्र',
        value: '1st and 10th emphasis often ties Mercury to persona and career craft.',
      },
      {
        label: '6th detail work',
        hi: 'छठा',
        value: 'Service, editing, troubleshooting, Mercury can excel where precision matters.',
      },
    ],
    lifeLayer: [
      { label: 'Represents', hi: 'कारकत्व', value: 'Markets, messengers, green (young) things, hybrid roles' },
      { label: 'Body parts (study map)', hi: 'अंग', value: 'Skin, speech organs, study only' },
      { label: 'Time & results', hi: 'काल / फल', value: 'Wednesday; quick turns in business cycles' },
      { label: 'Transit note', hi: 'गोचर', value: 'Mercury retrograde periods famous in pop culture, still read your own chart first.' },
    ],
    craftSymbols: [
      { label: 'Profession flavour', hi: 'व्यवसाय', value: 'Accounting, IT, writing, comedy, brokerage, analytics' },
      { label: 'Colour', hi: 'रंग', value: 'Green, mixed tones' },
      { label: 'Metal', hi: 'धातु', value: 'Bronze / mixed alloys (traditional lists)' },
      { label: 'Direction', hi: 'दिशा', value: 'North' },
      { label: 'Day', hi: 'वार', value: 'Wednesday (Budhavaara)' },
    ],
    behaviorBullets: [
      'Curious, witty, can overthink or multitask into fatigue.',
      'Best quality: bridges ideas, learns fast, charms with words.',
      'Rough edge: anxiety, sarcasm, or inconsistency when afflicted.',
    ],
    oneLiner: ' “Budha turns noise into signal, train the mind like an instrument.”',
    oneLinerNote: 'Combustion and sign element change the timbre.',
  },

  Jupiter: {
    titleEn: 'Guru (Jupiter), must-know points',
    taglineHi: 'गुरु: ज्ञान, विस्तार, आशीर्वाद',
    heroEmoji: '🙏',
    intro:
      'The great benefic: wisdom, children, teachers, and faith. Jupiter expands whatever it touches, generous when wise, excessive when unchecked.',
    goldenRule: 'Jupiter asks for growth with meaning, not bigger for bigger’s sake.',
    goldenRuleNote: 'Even a strong Jupiter can inflate pride; dignity and house steer the sermon.',
    reassuring: [
      {
        title: 'Grace has homework',
        titleHi: 'कृपा व कर्म',
        body: 'Classical Jupiter is protective, but charts still ask effort; “benefic” is not a free pass from responsibility.',
      },
      {
        title: 'Teachers everywhere',
        titleHi: 'गुरु',
        body: 'Significator of guides and dharma, can show up as people, books, or life events that reframe belief.',
      },
      {
        title: 'Children & wealth of spirit',
        titleHi: 'संतान',
        body: 'Karaka for children and wisdom-wealth; modern readers also map optimism and higher education.',
      },
    ],
    coreFacts: [
      { label: 'Karaka (significator)', hi: 'कारक', value: 'Wisdom, teachers, children, wealth of merit, liver, fat' },
      { label: 'Nature', hi: 'स्वभाव', value: 'Natural benefic, expansive, saattvic tone in classics' },
      { label: 'Speed', hi: 'गति', value: 'About one year per sign' },
      { label: 'Rashi ownership', hi: 'राशि स्वामी', value: 'धनु व मीन, दो स्वामित्व राशियाँ' },
      { label: 'Exaltation', hi: 'उच्च', value: 'कर्क में उच्च; शास्त्रीय रूप से लगभग ५°' },
      { label: 'Debilitation', hi: 'नीच', value: 'मकर में नीच' },
      { label: 'Mooltrikona', hi: 'मूलत्रिकोण', value: 'धनु, प्रायः ०° – १०° का बैंड' },
      { label: 'Drishti (aspects)', hi: 'दृष्टि', value: '5th, 7th, and 9th from its sign' },
      { label: 'Vakri (retrograde)', hi: 'वक्र', value: 'Retrograde Jupiter: inner review of beliefs, teachers, and risk appetite' },
      { label: 'Digbala', hi: 'दिग्बल', value: 'Strongest in the 1st house in standard digbala lists' },
    ],
    friendsEnemies: [
      { label: 'Friends', hi: 'मित्र', grahas: 'सूर्य, चंद्र, मंगल' },
      { label: 'Enemy', hi: 'शत्रु', grahas: 'बुध, शुक्र' },
      { label: 'Neutral', hi: 'सम', grahas: 'शनि' },
    ],
    housesNote: [
      {
        label: 'Kendra / trikona joy',
        hi: 'केन्द्र / त्रिकोण',
        value: 'Angular or trinal Jupiter often supports guidance and recovery stories, still read aspects.',
      },
      {
        label: '6th / 8th nuance',
        hi: 'दुःस्थान',
        value: 'Can show wisdom through service or crisis, tone from full chart, not fear labels.',
      },
    ],
    lifeLayer: [
      { label: 'Represents', hi: 'कारकत्व', value: 'Temples, law (dharma), gold, yellow foods, horses (traditional)' },
      { label: 'Body parts (study map)', hi: 'अंग', value: 'Liver, thighs, study only' },
      { label: 'Time & results', hi: 'काल / फल', value: 'Thursday; slower ripening gifts in dasha' },
      { label: 'Transit note', hi: 'गोचर', value: 'Jupiter’s sign change is a headline year for many, for study, not panic.' },
    ],
    craftSymbols: [
      { label: 'Profession flavour', hi: 'व्यवसाय', value: 'Teaching, law, counselling, philanthropy, publishing' },
      { label: 'Colour', hi: 'रंग', value: 'Yellow, saffron' },
      { label: 'Metal', hi: 'धातु', value: 'Gold (traditional)' },
      { label: 'Direction', hi: 'दिशा', value: 'North-east (eeshaanya, common classical note)' },
      { label: 'Day', hi: 'वार', value: 'Thursday (Guruvaara)' },
    ],
    behaviorBullets: [
      'Optimistic, moral compass, can preach if unchecked.',
      'Best quality: generosity, faith, big-picture calm.',
      'Rough edge: overpromising, weight gain metaphors, blind optimism.',
    ],
    oneLiner: ' “Guru expands the room, make sure the foundation can hold it.”',
    oneLinerNote: 'Retrograde Guru often turns the teacher inward first.',
  },

  Venus: {
    titleEn: 'Shukra (Venus), must-know points',
    taglineHi: 'शुक्र: सौंदर्य, प्रेम, संगीत',
    heroEmoji: '✨',
    intro:
      'Harmony, arts, partnership, and the sweetness of life. Venus is a natural benefic, charming when balanced, slippery when avoidance replaces honesty.',
    goldenRule: 'Venus teaches reciprocity: what you beautify inwardly shows outwardly.',
    goldenRuleNote: 'Sign, combustion, and malefic aspects change romance into repair work, both are human.',
    reassuring: [
      {
        title: 'Pleasure is sacred',
        titleHi: 'सुख पवित्र',
        body: 'Classical texts link Venus to shringgaara rasa, beauty and devotion are valid life paths, not frivolity by default.',
      },
      {
        title: 'Partnership maps',
        titleHi: 'साझेदारी',
        body: 'Karaka for spouse and agreements, useful for seeing relationship style, not predicting soap opera.',
      },
      {
        title: 'Art heals',
        titleHi: 'कला',
        body: 'Music, design, and gentle routine often steady Venus-heavy charts in real life.',
      },
    ],
    coreFacts: [
      { label: 'Karaka (significator)', hi: 'कारक', value: 'Spouse (for men in classical frame), arts, vehicles, comforts, semen, kidneys' },
      { label: 'Nature', hi: 'स्वभाव', value: 'Natural benefic, kapha/vaata sweet imagery in Aayurvedic cross-notes' },
      { label: 'Speed', hi: 'गति', value: 'About a month per sign (rough average)' },
      { label: 'Rashi ownership', hi: 'राशि स्वामी', value: 'वृषभ व तुला, दो स्वामित्व राशियाँ' },
      { label: 'Exaltation', hi: 'उच्च', value: 'मीन में उच्च; शास्त्रीय रूप से लगभग २७°' },
      { label: 'Debilitation', hi: 'नीच', value: 'कन्या में नीच' },
      { label: 'Mooltrikona', hi: 'मूलत्रिकोण', value: 'तुला, प्रायः ०° – १५° का बैंड' },
      { label: 'Drishti (aspects)', hi: 'दृष्टि', value: '7th from its sign only (full aspect)' },
      { label: 'Vakri (retrograde)', hi: 'वक्र', value: 'Retrograde Venus: revisit love, money, and aesthetics' },
      { label: 'Digbala', hi: 'दिग्बल', value: 'Strongest in the 4th house in standard digbala lists' },
    ],
    friendsEnemies: [
      { label: 'Friends', hi: 'मित्र', grahas: 'बुध, शनि' },
      { label: 'Enemy', hi: 'शत्रु', grahas: 'सूर्य, चंद्र' },
      { label: 'Neutral', hi: 'सम', grahas: 'मंगल, गुरु' },
    ],
    housesNote: [
      {
        label: '2nd / 7th stories',
        hi: 'धन / युवति',
        value: 'Wealth of voice and partnership houses often echo Venus themes, read holistically.',
      },
      {
        label: '12th transcendence',
        hi: 'द्वादश',
        value: 'Bed pleasures, sleep, foreign shores, Venus here is nuanced, not automatically “loss.”',
      },
    ],
    lifeLayer: [
      { label: 'Represents', hi: 'कारकत्व', value: 'Silk, perfumes, cows (traditional), diplomacy' },
      { label: 'Body parts (study map)', hi: 'अंग', value: 'Reproductive system, eyes, study only' },
      { label: 'Time & results', hi: 'काल / फल', value: 'Friday; relationship and cash flow rhythms' },
      { label: 'Transit note', hi: 'गोचर', value: 'Venus transits colour social warmth and creative urges for a few weeks per sign.' },
    ],
    craftSymbols: [
      { label: 'Profession flavour', hi: 'व्यवसाय', value: 'Design, music, fashion, diplomacy, luxury trades' },
      { label: 'Colour', hi: 'रंग', value: 'Bright colours, white, pastels' },
      { label: 'Metal', hi: 'धातु', value: 'Silver / platinum tones in modern glossaries' },
      { label: 'Direction', hi: 'दिशा', value: 'South-east (common note)' },
      { label: 'Day', hi: 'वार', value: 'Friday (Shukravaara)' },
    ],
    behaviorBullets: [
      'Seeks ease, beauty, and fair exchange.',
      'Best quality: grace, artistry, peacemaking.',
      'Rough edge: indulgence, avoidance, or people-pleasing when afflicted.',
    ],
    oneLiner: ' “Shukra says: polish the heart, not only the mirror.”',
    oneLinerNote: 'Combust Venus is a classical friction point, check orb and sign.',
  },

  Saturn: {
    titleEn: 'Shani (Saturn), must-know points',
    taglineHi: 'शनि: धैर्य, कर्तव्य, और स्थिर फल',
    heroEmoji: '🪐',
    intro:
      'Use this page as a study sheet, not a verdict. Shani speaks in the language of time, duty, and honesty, frightening when we resist growth, grounding when we cooperate with the lesson.',
    goldenRule: '“Shani gives exactly what you deserve, karma-based, not chaotic malice.”',
    goldenRuleNote: 'Fairness here means consistency: effort, ethics, and patience tend to weigh in your favour over the long arc.',
    reassuring: [
      {
        title: 'Teacher, not tormentor',
        titleHi: 'दंड नहीं, दिशा',
        body: 'Classical Jyotish often frames Shani as karma-phala, the graha that settles accounts with patience. Pressure can feel sharp, but the aim is maturity, boundaries, and lasting structure, not endless punishment.',
      },
      {
        title: 'Slow ≠ cruel',
        titleHi: 'धीमा = स्थिर',
        body: 'Saturn’s long stays and slow motion build endurance. What arrives late from Shani’s side is often hard to undo, both lessons and gains can be deep-rooted.',
      },
      {
        title: 'Chart context wins',
        titleHi: 'संदर्भ ज़रूरी',
        body: 'Sign, house, avastha, aspects, and dasha decide how Shani shows up. The same placement can mean disciplined craft for one chart and heavy fatigue for another, avoid one-word doom labels.',
      },
    ],
    coreFacts: [
      { label: 'Karaka (significator)', hi: 'कारक', value: 'Discipline, delay, karma, justice, hard work' },
      { label: 'Nature', hi: 'स्वभाव', value: 'Natural malefic, still gives long-term rewards when dignity and effort align' },
      { label: 'Speed', hi: 'गति', value: 'Slowest major graha → delay, pacing, and stability over time' },
      { label: 'Rashi ownership', hi: 'राशि स्वामी', value: 'मकर व कुंभ, दो स्वामित्व राशियाँ' },
      { label: 'Exaltation', hi: 'उच्च', value: 'तुला में उच्च' },
      { label: 'Debilitation', hi: 'नीच', value: 'मेष में नीच' },
      { label: 'Mooltrikona', hi: 'मूलत्रिकोण', value: 'कुंभ, प्रायः ०° – २०° का बैंड (पाठशालाएँ थोड़ा भिन्न)' },
      { label: 'Drishti (aspects)', hi: 'दृष्टि', value: '3rd, 7th, and 10th from its sign' },
      { label: 'Vakri (retrograde)', hi: 'वक्र', value: 'Interpreted as more karmic / introspective / intense, synthesize with full chart' },
      { label: 'Digbala', hi: 'दिग्बल', value: 'Strongest in the 7th house (yuvati bhava) in standard digbala lists' },
    ],
    friendsEnemies: [
      { label: 'Friends', hi: 'मित्र', grahas: 'बुध, शुक्र' },
      { label: 'Enemy', hi: 'शत्रु', grahas: 'सूर्य, चंद्र, मंगल' },
      { label: 'Neutral', hi: 'सम', grahas: 'गुरु' },
    ],
    housesNote: [
      {
        label: 'Trika / dusthana link',
        hi: 'दुःखस्थान',
        value: 'Often associated with themes of 6th, 8th, 12th, service, transformation, release (not “only bad luck”).',
      },
      {
        label: 'Upachaya',
        hi: 'उपचय',
        value: 'Growth houses 3, 6, 10, 11, Saturn can strengthen effort-over-time stories here when supported.',
      },
    ],
    lifeLayer: [
      { label: 'Represents', hi: 'कारकत्व', value: 'Masses, labour, servants, elderly, humility, institutions' },
      { label: 'Body parts (study map)', hi: 'अंग', value: 'Legs, bones, teeth, nerves, traditional correspondences; not medical advice' },
      { label: 'Health flavour', hi: 'रूगण', value: 'Chronic, slow-onset patterns in classical glossaries, always see professionals for health' },
      { label: 'Time & results', hi: 'काल / फल', value: 'Late but often durable outcomes when merit and effort meet Shani’s tempo' },
      { label: 'Sade Sati', hi: 'साढ़े साती', value: '~7.5-year brush with Moon’s sign ±1, major life review for many; read with Gochar + chart' },
      { label: 'Transit', hi: 'गोचर', value: 'Shani’s sign changes are landmarks for career, duty, and boundaries' },
    ],
    craftSymbols: [
      { label: 'Profession (karakatva)', hi: 'व्यवसाय', value: 'Labour, engineering, mining, law, justice, machinery, administration' },
      { label: 'Colour', hi: 'रंग', value: 'Black, dark blue, deep greys' },
      { label: 'Metal', hi: 'धातु', value: 'Iron / steel (traditional lists)' },
      { label: 'Direction', hi: 'दिशा', value: 'West (common classical note)' },
      { label: 'Day', hi: 'वार', value: 'Saturday (Shanivar)' },
    ],
    behaviorBullets: [
      'Tests patience, responsibility, and follow-through, not entertainment-value dasha.',
      'Best quality: makes you measured, credible, and durable.',
      'Rough edge: fear, delay, constriction when the chart shows frictions, remedy paths are cultural (fast, seva, ethical labour), not one-size-fits-all.',
    ],
    oneLiner: ' “Shani delays, tests, but never denies if karma is right.”',
    oneLinerNote: 'Translation for nervous beginners: slow is not “no.” Keep showing up with integrity; let the full kundali tell the pace.',
  },

  Rahu: {
    titleEn: 'Rahu, must-know points',
    taglineHi: 'राहु: इच्छा, विस्तार, अनोखा रास्ता',
    heroEmoji: '◐',
    intro:
      'The north node: appetite, obsession, foreign textures, and plot twists. Rahu is not a physical planet but a sensitive lunar point, intense, cinematic, and deeply chart-dependent.',
    goldenRule: 'Rahu magnifies whatever it touches, aim the hunger toward skill and ethics, not endless scrolling.',
    goldenRuleNote: 'Exaltation signs and co-lord arguments differ by aacaarya; always read dispositor, sign, and house together.',
    reassuring: [
      {
        title: 'Desire is not sin',
        titleHi: 'इच्छा',
        body: 'Classical frames treat Rahu as smoke and illusion, yet the same energy drives innovation and breakthroughs when channelled.',
      },
      {
        title: 'Foreign / new',
        titleHi: 'नवीन',
        body: 'Karaka for strangers, travel, and rule-bending paths, helpful metaphor, not a stigma tag.',
      },
      {
        title: 'With Ketu as axis',
        titleHi: 'अक्ष',
        body: 'Rahu-Ketu axis splits hunger vs release across opposite houses; synthesis beats fear.',
      },
    ],
    coreFacts: [
      { label: 'Karaka (significator)', hi: 'कारक', value: 'Desire, ambition, foreigners, tech, fame, intoxication (traditional cautions)' },
      { label: 'Nature', hi: 'स्वभाव', value: 'Shadow graha, malefic tone in most lists, yet can grant rise when yoga supports' },
      { label: 'Speed', hi: 'गति', value: 'Retrograde by primary motion; ~1.5 years per sign (rough)' },
      { label: 'Rashi ownership', hi: 'राशि स्वामी', value: 'क्लासिकल राशि स्वामित्व नहीं, कुछ बाद के ग्रंथ कुंभ सह-स्वामी पर चर्चा करते हैं' },
      { label: 'Exaltation / debilitation', hi: 'उच्च / नीच', value: 'पाठशालाएँ भिन्न: वृषभ / मिथुन / वृश्चिक आदि, अपनी परंपरा की सारणी देखें' },
      { label: 'Mooltrikona', hi: 'मूलत्रिकोण', value: 'Not standard like physical grahas, follow your teacher’s framework' },
      {
        label: 'Drishti (aspects)',
        hi: 'दृष्टि',
        value: 'Commonly treated like Jupiter (5th, 7th, 9th) in North Indian commentaries, verify your tradition',
      },
      { label: 'Vakri (retrograde)', hi: 'वक्र', value: 'Always retrograde in usual chart calculation, intensifies recursive craving themes' },
      { label: 'Digbala', hi: 'दिग्बल', value: 'Often discussed indirectly through sign/house strength rather than classic digbala row' },
    ],
    friendsEnemies: null,
    friendsEnemiesNote:
      'Rahu-Ketu friendship tables vary; many readers infer behaviour from sign lord, conjunct planets, and Saturn/Mars analogies.',
    housesNote: [
      {
        label: 'Axis houses',
        hi: 'अक्ष भाव',
        value: 'Opposite house from Ketu shows where letting go balances Rahu’s hunger, read both ends.',
      },
      {
        label: '10th / 11th fame motifs',
        hi: 'कीर्ति',
        value: 'Modern readings link Rahu to platforms, networks, and visibility, always ethical context.',
      },
    ],
    lifeLayer: [
      { label: 'Represents', hi: 'कारकत्व', value: 'Snake imagery, smoke, outcasts, radical change' },
      { label: 'Body parts (study map)', hi: 'अंग', value: 'Skin, epidemics (traditional stress lists), not medical advice' },
      { label: 'Time & results', hi: 'काल / फल', value: 'Rahu kaala / eclipse seasons, cultural timing tools; chart first' },
      { label: 'Transit note', hi: 'गोचर', value: 'Nodal shifts move the eclipse axis every ~18 months, life chapter markers for many' },
    ],
    craftSymbols: [
      { label: 'Profession flavour', hi: 'व्यवसाय', value: 'Research, media, imports, aviation, unconventional careers' },
      { label: 'Colour', hi: 'रंग', value: 'Smoke grey, iridescent, unconventional mixes' },
      { label: 'Metal', hi: 'धातु', value: 'Mixed / alloy (traditional smoke-and-mirror metaphor)' },
      { label: 'Direction', hi: 'दिशा', value: 'South-west (common note)' },
      { label: 'Day', hi: 'वार', value: 'Saturday often linked in remedial culture, follow your lineage' },
    ],
    behaviorBullets: [
      'Hungry for experience; bends rules; learns through repetition.',
      'Best quality: innovation, nerve, outsider genius.',
      'Rough edge: fixation, shortcuts, or identity fog when afflicted.',
    ],
    oneLiner: ' “Rahu enlarges the story, pick the script consciously.”',
    oneLinerNote: 'Eclipse seasons and nodal return years are study windows, not omens.',
  },

  Ketu: {
    titleEn: 'Ketu, must-know points',
    taglineHi: 'केतु: मोक्ष, विरक्ति, अंतर्दृष्टि',
    heroEmoji: '◑',
    intro:
      'The south node: inwardness, release, and sharp insight. Ketu subtracts to teach, sometimes through loss metaphors, sometimes through sudden clarity.',
    goldenRule: 'Ketu asks what you are ready to stop gripping, freedom follows skillful letting go.',
    goldenRuleNote: 'With Mars-like aspect lists in many commentaries, Ketu can cut cleanly; combine with sign lord and house.',
    reassuring: [
      {
        title: 'Detachment ≠ coldness',
        titleHi: 'वैराग्य',
        body: 'Spiritual texts praise healthy vairaagya. Ketu can show meditation, research, and integrity without performance.',
      },
      {
        title: 'Past-life colour',
        titleHi: 'पूर्व जन्म',
        body: 'Traditional language uses samskaara stories, use as reflective metaphor, not fatalism.',
      },
      {
        title: 'Balance with Rahu',
        titleHi: 'संतुलन',
        body: 'Where Rahu grasps, Ketu releases; a balanced life dialogues between both houses.',
      },
    ],
    coreFacts: [
      { label: 'Karaka (significator)', hi: 'कारक', value: 'moksha, mysticism, wounds, sudden events, mathematics, dogs (traditional)' },
      { label: 'Nature', hi: 'स्वभाव', value: 'Shadow graha, separative, subtle, karmic in tone' },
      { label: 'Speed', hi: 'गति', value: 'Retrograde by primary motion; ~1.5 years per sign (rough)' },
      { label: 'Rashi ownership', hi: 'राशि स्वामी', value: 'क्लासिकल राशि स्वामित्व नहीं, कुछ बाद के ग्रंथ वृश्चिक सह-स्वामी पर चर्चा करते हैं' },
      { label: 'Exaltation / debilitation', hi: 'उच्च / नीच', value: 'पाठशालाएँ भिन्न: वृश्चिक / धनु आदि, अपनी परंपरा की सारणी देखें' },
      { label: 'Mooltrikona', hi: 'मूलत्रिकोण', value: 'Not standard like physical grahas, follow your teacher’s framework' },
      {
        label: 'Drishti (aspects)',
        hi: 'दृष्टि',
        value: 'Commonly treated like Mars (4th, 7th, 8th) in commentaries, verify your tradition',
      },
      { label: 'Vakri (retrograde)', hi: 'वक्र', value: 'Always retrograde in usual chart calculation' },
      { label: 'Digbala', hi: 'दिग्बल', value: 'Assessed via house/sign strength and conjunctions in practice' },
    ],
    friendsEnemies: null,
    friendsEnemiesNote:
      'Ketu’s relationships are inferred from placement; some texts parallel Mars or Saturn tones.',
    housesNote: [
      {
        label: '12th / 8th themes',
        hi: 'मोक्ष स्थान',
        value: 'Loss, liberation, and research houses echo Ketu’s vocabulary, context decides tone.',
      },
      {
        label: '1st intuition',
        hi: 'लग्न',
        value: 'Ketu on ascendant can mark inner identity quests, not a fixed “bad” label.',
      },
    ],
    lifeLayer: [
      { label: 'Represents', hi: 'कारकत्व', value: 'Monks, flags, detachment, sharp eyes (traditional)' },
      { label: 'Body parts (study map)', hi: 'अंग', value: 'Ankles, mysterious pains in glossaries, professionals for real health' },
      { label: 'Time & results', hi: 'काल / फल', value: 'Ketu sub-periods can feel abrupt, full dasha tree needed' },
      { label: 'Transit note', hi: 'गोचर', value: 'Eclipse points highlight Ketu’s house topics for a season' },
    ],
    craftSymbols: [
      { label: 'Profession flavour', hi: 'व्यवसाय', value: 'Research, spirituality, diagnostics, behind-scenes tech' },
      { label: 'Colour', hi: 'रंग', value: 'Smoky brown, grey, multicolour' },
      { label: 'Metal', hi: 'धातु', value: 'Mixed metals (traditional)' },
      { label: 'Direction', hi: 'दिशा', value: 'North-west (common note)' },
      { label: 'Day', hi: 'वार', value: 'Tuesday links in some remedial lists, lineage dependent' },
    ],
    behaviorBullets: [
      'Quiet, sceptical, insightful; dislikes empty ritual without meaning.',
      'Best quality: focus, spiritual grit, pattern recognition.',
      'Rough edge: isolation, cuts ties sharply when afflicted.',
    ],
    oneLiner: ' “Ketu removes the costume, what remains is truer work.”',
    oneLinerNote: 'Opposite house from Rahu tells what life wants you to grow toward.',
  },
};
